// @ts-check

export const intervalDefaults = {
  workSeconds: 30,
  restSeconds: 15,
  repetitions: 8,
};

export const reactionDefaults = {
  minimumSeconds: 5,
  maximumSeconds: 8,
};

export const timerLimits = {
  durationMinimum: 1,
  durationMaximum: 3600,
  repetitionsMinimum: 1,
  repetitionsMaximum: 99,
  reactionMaximum: 600,
};

/**
 * @param {URLSearchParams} params
 * @returns {{ workSeconds: number, restSeconds: number, repetitions: number }}
 */
export function getIntervalConfigFromParams(params) {
  const workSeconds = readBoundedInteger(
    params.get("work"),
    intervalDefaults.workSeconds,
    timerLimits.durationMinimum,
    timerLimits.durationMaximum,
  );
  const restSeconds = readBoundedInteger(
    params.get("rest"),
    intervalDefaults.restSeconds,
    timerLimits.durationMinimum,
    timerLimits.durationMaximum,
  );
  const repetitions = readBoundedInteger(
    params.get("repetitions"),
    intervalDefaults.repetitions,
    timerLimits.repetitionsMinimum,
    timerLimits.repetitionsMaximum,
  );

  return { workSeconds, restSeconds, repetitions };
}

/**
 * @param {URLSearchParams} params
 * @returns {{ minimumSeconds: number, maximumSeconds: number }}
 */
export function getReactionConfigFromParams(params) {
  const minimumSeconds = readBoundedInteger(
    params.get("minimum"),
    reactionDefaults.minimumSeconds,
    timerLimits.durationMinimum,
    timerLimits.reactionMaximum,
  );
  let maximumSeconds = readBoundedInteger(
    params.get("maximum"),
    reactionDefaults.maximumSeconds,
    timerLimits.durationMinimum,
    timerLimits.reactionMaximum,
  );

  if (maximumSeconds < minimumSeconds) {
    maximumSeconds = minimumSeconds;
  }

  return { minimumSeconds, maximumSeconds };
}

/**
 * @param {{ workSeconds: number, restSeconds: number, repetitions: number }} config
 * @returns {number}
 */
export function getIntervalTotalSeconds(config) {
  const workDuration = config.workSeconds * config.repetitions;
  const restCount = Math.max(0, config.repetitions - 1);
  const restDuration = config.restSeconds * restCount;
  return workDuration + restDuration;
}

/**
 * @param {{ workSeconds: number, restSeconds: number, repetitions: number }} config
 * @param {number} elapsedSeconds
 * @returns {{ phase: "work" | "rest" | "complete", key: string, round: number, remainingSeconds: number, phaseDurationSeconds: number, progress: number }}
 */
export function getIntervalSnapshot(config, elapsedSeconds) {
  const totalSeconds = getIntervalTotalSeconds(config);
  const boundedElapsed = clamp(elapsedSeconds, 0, totalSeconds);
  let snapshot = {
    phase: /** @type {"work" | "rest" | "complete"} */ ("complete"),
    key: "complete",
    round: config.repetitions,
    remainingSeconds: 0,
    phaseDurationSeconds: config.workSeconds,
    progress: 1,
  };

  if (boundedElapsed >= totalSeconds) {
    return snapshot;
  }

  let phaseStart = 0;
  for (let round = 1; round <= config.repetitions; round += 1) {
    const workEnd = phaseStart + config.workSeconds;
    if (boundedElapsed < workEnd) {
      const phaseElapsed = boundedElapsed - phaseStart;
      snapshot = createSnapshot(
        "work",
        round,
        config.workSeconds,
        phaseElapsed,
      );
      break;
    }

    phaseStart = workEnd;
    if (round === config.repetitions) {
      continue;
    }

    const restEnd = phaseStart + config.restSeconds;
    if (boundedElapsed < restEnd) {
      const phaseElapsed = boundedElapsed - phaseStart;
      snapshot = createSnapshot(
        "rest",
        round,
        config.restSeconds,
        phaseElapsed,
      );
      break;
    }

    phaseStart = restEnd;
  }

  return snapshot;
}

/**
 * @param {number} minimumSeconds
 * @param {number} maximumSeconds
 * @returns {number}
 */
export function getRandomDelaySeconds(minimumSeconds, maximumSeconds) {
  const range = Math.max(0, maximumSeconds - minimumSeconds);
  return minimumSeconds + Math.random() * range;
}

/**
 * @param {number} seconds
 * @returns {string}
 */
export function formatClock(seconds) {
  const displaySeconds = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(displaySeconds / 60);
  const secondsPart = displaySeconds % 60;
  const paddedSeconds = String(secondsPart).padStart(2, "0");
  return `${String(minutes).padStart(2, "0")}:${paddedSeconds}`;
}

/**
 * @param {"work" | "rest"} phase
 * @param {number} round
 * @param {number} durationSeconds
 * @param {number} elapsedSeconds
 * @returns {{ phase: "work" | "rest", key: string, round: number, remainingSeconds: number, phaseDurationSeconds: number, progress: number }}
 */
function createSnapshot(phase, round, durationSeconds, elapsedSeconds) {
  const remainingSeconds = Math.max(0, durationSeconds - elapsedSeconds);
  const progress = clamp(elapsedSeconds / durationSeconds, 0, 1);
  const key = `${phase}-${round}`;

  return {
    phase,
    key,
    round,
    remainingSeconds,
    phaseDurationSeconds: durationSeconds,
    progress,
  };
}

/**
 * @param {string | null} rawValue
 * @param {number} fallback
 * @param {number} minimum
 * @param {number} maximum
 * @returns {number}
 */
function readBoundedInteger(rawValue, fallback, minimum, maximum) {
  let value = fallback;
  const hasValue = rawValue !== null && rawValue.trim() !== "";

  if (hasValue) {
    const parsedValue = Number(rawValue);
    if (Number.isFinite(parsedValue)) {
      value = parsedValue;
    }
  }

  value = Math.round(value);
  return clamp(value, minimum, maximum);
}

/**
 * @param {number} value
 * @param {number} minimum
 * @param {number} maximum
 * @returns {number}
 */
function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}
